import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppStore } from "@/lib/store";
import { api } from "../../lib/api/client";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Plus, Search, Loader2, Copy, Check, FolderOpen, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { useSessions, useSession, useTenants } from "../../lib/api/hooks";
import { useStreamStore } from "../../lib/streamStore";

interface SessionsSidebarProps {
  onSessionSelect?: () => void;
}

/**
 * Fuzzy match function - returns true if query characters appear in text in order
 * Allows gaps between characters for flexible matching
 */
function fuzzyMatch(text: string, query: string): boolean {
  if (!query) return true;
  const t = text.toLowerCase();
  const q = query.toLowerCase();
  let tIndex = 0;
  let qIndex = 0;

  while (tIndex < t.length && qIndex < q.length) {
    if (t[tIndex] === q[qIndex]) {
      qIndex++;
    }
    tIndex++;
  }

  return qIndex === q.length;
}

/**
 * Get match indices for highlighting - returns array of indices where query matches text
 */
function getMatchIndices(text: string, query: string): number[] {
  if (!query) return [];
  const t = text.toLowerCase();
  const q = query.toLowerCase();
  const indices: number[] = [];
  let tIndex = 0;
  let qIndex = 0;

  while (tIndex < t.length && qIndex < q.length) {
    if (t[tIndex] === q[qIndex]) {
      indices.push(tIndex);
      qIndex++;
    }
    tIndex++;
  }

  return qIndex === q.length ? indices : [];
}

/**
 * Highlight matching parts of text with a span
 */
function highlightMatch(text: string, query: string, className: string): React.ReactNode {
  const indices = getMatchIndices(text, query);
  if (indices.length === 0) return text;

  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  indices.forEach((index, i) => {
    // Add non-matching text before this match
    if (index > lastIndex) {
      result.push(text.slice(lastIndex, index));
    }
    // Add matching character
    result.push(
      <span key={i} className={className}>
        {text[index]}
      </span>
    );
    lastIndex = index + 1;
  });

  // Add remaining text
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
}

export function SessionsSidebar({ onSessionSelect }: SessionsSidebarProps) {
  const { selectedSessionId, setSelectedSessionId, selectedModel } = useAppStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [newSessionOpen, setNewSessionOpen] = useState(false);
  const [newSessionTenant, setNewSessionTenant] = useState("demo");
  const [copiedSessionDir, setCopiedSessionDir] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: selectedSession } = useSession(selectedSessionId);
  const streamStateBySession = useStreamStore((state) => state.streamStateBySession);

  const { data: sessions = [], isLoading, isError, error } = useSessions();
  const { data: tenantsData } = useTenants();
  const tenants = tenantsData?.tenants || [];

  const formatSessionName = (sessionId: string) => {
    // Extract readable part: plumcot-demo-20251230T123456Z-abc123 -> 2025-12-30 12:34
    const match = sessionId.match(/plumcot-\w+-(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})\d+Z/);
    if (match) {
      const [, year, month, day, hour, minute] = match;
      return `${year}-${month}-${day} ${hour}:${minute}`;
    }
    // Fallback: show last part
    return sessionId.split("-").slice(-2).join("-");
  };

  const createSessionMutation = useMutation({
    mutationFn: (tenant: string) =>
      api.createSession({ tenant, trace: false, live: false, model: selectedModel }),
    onSuccess: (newSession) => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      setSelectedSessionId(newSession.session_id);
      setNewSessionOpen(false);
      onSessionSelect?.();
    },
  });

  const deleteSessionMutation = useMutation({
    mutationFn: (sessionId: string) => api.deleteSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      if (selectedSessionId === deleteConfirmOpen) {
        setSelectedSessionId(null);
      }
      setDeleteConfirmOpen(null);
    },
  });

  const filteredSessions = !searchQuery.trim()
    ? sessions
    : sessions.filter((s) => {
        const formattedName = formatSessionName(s.session_id);
        return (
          fuzzyMatch(formattedName, searchQuery) ||
          fuzzyMatch(s.session_id, searchQuery) ||
          fuzzyMatch(s.tenant, searchQuery) ||
          (s.first_message && fuzzyMatch(s.first_message, searchQuery))
        );
      });

  // Sort by last_modified (or last_activity, or created_at) - most recent first
  const sortedSessions = [...filteredSessions].sort((a, b) => {
    const aTime = new Date(a.last_modified || a.last_activity || a.created_at).getTime();
    const bTime = new Date(b.last_modified || b.last_activity || b.created_at).getTime();
    return bTime - aTime;  // Descending order (newest first)
  });

  const handleNewSession = () => {
    createSessionMutation.mutate(newSessionTenant);
  };

  const handleCopySessionDir = async () => {
    if (!selectedSession?.session_dir) return;
    try {
      await navigator.clipboard.writeText(selectedSession.session_dir);
      setCopiedSessionDir(selectedSession.session_dir);
      setTimeout(() => setCopiedSessionDir(null), 2000);
    } catch (error) {
      console.error("Failed to copy session dir:", error);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    setDeleteConfirmOpen(sessionId);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmOpen) {
      deleteSessionMutation.mutate(deleteConfirmOpen);
    }
  };

  return (
    <div className="flex h-full flex-col bg-card">
      {/* Header */}
      <div className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-2 p-4">
          <Button
              size="icon"
              onClick={() => setNewSessionOpen(true)}
              title="New Session"
              className="h-9 w-9 shrink-0"
            >
              <Plus className="h-4 w-4" />
            </Button>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Fuzzy search sessions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 text-sm shadow-sm"
            />
          </div>

        </div>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">Loading sessions...</p>
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mb-3">
              <Search className="h-6 w-6 text-destructive" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">
              Failed to load sessions
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              {error?.message || "Unknown error"}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="text-xs text-primary hover:underline"
            >
              Retry
            </button>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
              <Search className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">
              {searchQuery ? "No sessions found" : "No sessions yet"}
            </p>
            {!searchQuery && (
              <p className="text-xs text-muted-foreground">
                Create a new session to get started
              </p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {sortedSessions.map((session) => {
              const isSelected = selectedSessionId === session.session_id;
              const formattedName = formatSessionName(session.session_id);

              // Highlight matches in search results
              const displayName = searchQuery.trim()
                ? highlightMatch(formattedName, searchQuery, isSelected ? "bg-primary-foreground/30 rounded-sm" : "bg-yellow-200/70 rounded-sm")
                : formattedName;

              const displayTenant = searchQuery.trim()
                ? highlightMatch(session.tenant, searchQuery, isSelected ? "bg-primary-foreground/30 rounded-sm" : "bg-yellow-200/70 rounded-sm")
                : session.tenant;

              const displayFirstMessage = session.first_message && searchQuery.trim()
                ? highlightMatch(
                    session.first_message.length > 100
                      ? session.first_message.slice(0, 100)
                      : session.first_message,
                    searchQuery,
                    isSelected ? "bg-primary-foreground/30 rounded-sm" : "bg-yellow-200/70 rounded-sm"
                  )
                : session.first_message;

              return (
                <div
                  key={session.session_id}
                  className={`group relative transition-colors ${isSelected
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-accent text-foreground"
                    }`}
                >
                  <button
                    onClick={() => {
                      setSelectedSessionId(session.session_id);
                      onSessionSelect?.();
                    }}
                    className="w-full text-left p-3 space-y-2 relative"
                  >
                    {/* Header: Session Name + Tenant Badge */}
                    <div className="flex items-start justify-between gap-2">
                      {/* Session Name - Prominent */}
                      <div className={`font-semibold text-sm leading-tight flex-1 ${isSelected ? "text-primary-foreground" : "text-foreground"
                        }`}>
                        {displayName}
                      </div>

                      {/* Right side: Running indicator + Tenant Badge */}
                      <div className="flex items-center gap-2 shrink-0">
                        {/* Running indicator */}
                        {!!streamStateBySession[session.session_id]?.isStreaming && (
                          <div className={`flex items-center gap-1.5 ${isSelected ? "text-primary-foreground/80" : "text-muted-foreground"
                            }`}>
                            <Loader2 className="h-3 w-3 animate-spin" />
                            <span className="text-xs font-medium">Running</span>
                          </div>
                        )}

                        {/* Tenant Badge */}
                        <Badge
                          variant="secondary"
                          className={`text-xs ${isSelected
                              ? "bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30"
                              : ""
                            }`}
                        >
                          {displayTenant}
                        </Badge>
                      </div>
                    </div>

                    {/* First Message Preview */}
                    {displayFirstMessage && (
                      <div className={`text-xs leading-snug line-clamp-2 ${isSelected ? "text-primary-foreground/80" : "text-muted-foreground"
                        }`}>
                        {displayFirstMessage}
                        {session.first_message.length > 100 && "..."}
                      </div>
                    )}

                    {/* Last Activity */}
                    {session.last_activity && (
                      <div className={`text-xs ${isSelected ? "text-primary-foreground/70" : "text-muted-foreground"
                        }`}>
                        {new Date(session.last_activity).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    )}
                  </button>

                  {/* Delete Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => handleDeleteClick(e, session.session_id)}
                    className={`absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity ${isSelected
                        ? "text-primary-foreground hover:bg-primary-foreground/20"
                        : "text-muted-foreground hover:text-destructive"
                      }`}
                    title="Delete session"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Session Export Section */}
      {selectedSessionId && selectedSession && (
        <div className="border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 p-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Session Directory</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono text-foreground truncate bg-muted/50 px-2 py-1.5 rounded border border-border/50">
                  {selectedSession.session_dir}
                </div>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopySessionDir}
                title="Copy session directory path"
                className="h-8 w-8 shrink-0"
              >
                {copiedSessionDir === selectedSession.session_dir ? (
                  <Check className="h-3.5 w-3.5 text-green-600" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Session Dialog */}
      <Dialog open={newSessionOpen} onOpenChange={setNewSessionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Session</DialogTitle>
            <DialogDescription>
              Create a new session to start a conversation with the agent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Tenant</label>
              <Select value={newSessionTenant} onValueChange={setNewSessionTenant}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.tenant} value={tenant.tenant}>
                      {tenant.tenant}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNewSessionOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleNewSession}
              disabled={createSessionMutation.isPending}
            >
              {createSessionMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen !== null} onOpenChange={(open) => !open && setDeleteConfirmOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Session</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this session? This action cannot be undone.
              All conversation history, artifacts, and related data will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(null)}
              disabled={deleteSessionMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteSessionMutation.isPending}
            >
              {deleteSessionMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
